import React, { useContext, useEffect, useMemo, useState } from 'react'
import { actionAtom } from 'atoms/atoms'
import { useAtom, useSetAtom } from 'jotai'
import isEmpty from 'lodash-es/isEmpty'
import { useTranslation } from 'react-i18next'
import { useDebounce, useFeatureFlags } from '@node-space/hooks'
import { StatusCard } from '@node-space/storybook-components/dist/StatusCard'
import { TextAction } from '@node-space/storybook-components/dist/TextAction'
import { Avatar, Box, Button, Combobox, Icon, Input } from '@node-space/ui-components'
import { ContactsSearchList } from 'components/Contacts/ContactsSearchList'
import NoSearchResults from 'components/NoSearchResults/NoSearchResults'
import { Description } from 'components/text/Description'
import { useContactInstrumentsInfiniteQuery } from 'hooks/queries/useContactInstrumentsInfiniteQuery'
import { useWalletsQueryV2 } from 'hooks/queries/useWalletsQueryV2'
import { useActiveWallets } from 'hooks/useActiveWallets'
import { contactAtom, contactInstrumentAtom } from 'pages/Contacts/atoms/contactAtoms'
import { ContactsSheets } from 'pages/Contacts/components/ContactsSheets'
import { ContactsListSkeletonLoader } from 'pages/Contacts/ContactsListSkeletonLoader'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { Contact, ContactInstrument } from 'types/contacts'
import { ActionIds } from 'types/types'
import { SendStep } from 'types/walletActions'
import { floorWithPrecision } from 'utils/utils'
import { sendStepAtom, sendWalletAtom } from './atoms/atoms'

export const SendContact = () => {
  const { t } = useTranslation()

  const setContactInstrument = useSetAtom(contactInstrumentAtom)
  const setContact = useSetAtom(contactAtom)
  const setSendStep = useSetAtom(sendStepAtom)
  const [walletAction] = useContext(WalletActionContext)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchWalletQuery, setSearchWalletQuery] = useState('')
  const [walletError, setWalletError] = useState(false)
  const debouncedSearchTerm = useDebounce(searchQuery, 1000)
  const debouncedSearchWalletTerm = useDebounce(searchWalletQuery, 1000)
  const [sendWallet, setSendWallet] = useAtom(sendWalletAtom)
  const [hasLoadedMoreContacts, setHasLoadedMoreContacts] = useState(false)
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [action, setAction] = useAtom(actionAtom)
  const { enableTravelRuleSelfHosted } = useFeatureFlags()

  const { data: wallets, isFetching: isFetchingWallets } = useWalletsQueryV2({
    walletType: '',
    searchTerm: debouncedSearchWalletTerm,
    max: 100,
    order: 'asc',
    sort: 'description',
  })

  const {
    data: contactInstruments,
    fetchNextPage,
    hasNextPage,
    isError,
    isPending,
    isFetchingNextPage,
  } = useContactInstrumentsInfiniteQuery(debouncedSearchTerm, enableTravelRuleSelfHosted)

  const activeWallets = useActiveWallets(wallets?.data)

  useEffect(() => {
    if (walletAction?.meta?.sourceWallet && wallets?.data && !sendWallet) {
      const sourceWallet = wallets?.data?.find(
        wallet => wallet?.lsid === walletAction?.meta?.sourceWallet?.lsid
      )

      if (!sourceWallet) wallets?.data?.unshift(walletAction?.meta?.sourceWallet)

      setSendWallet(walletAction?.meta?.sourceWallet)
    }
  }, [walletAction, wallets])

  const sourceWallets = useMemo(
    () => wallets?.data?.filter(wallet => wallet?.balance > 0),
    [activeWallets]
  )

  const sourceWalletOptions = useMemo(
    () =>
      sourceWallets?.map(wallet => ({
        LeftElement: <Avatar name={wallet?.currency?.code?.toLowerCase()} size="small" />,
        value: wallet?.lsid,
        leftText: wallet?.description,
        rightText: `${floorWithPrecision(wallet?.balance || 0, wallet?.currency?.quantityPrecision)} ${wallet?.currency?.code}`,
      })),
    [sourceWallets]
  )

  const noResults = debouncedSearchTerm && !contactInstruments?.length
  const showEmptyState =
    !isPending && !isError && !contactInstruments?.length && !debouncedSearchTerm
  const showErrorState = isError && !isPending
  const showNoSearchResults = noResults && !isPending
  const showSearch = !!contactInstruments?.length || noResults

  const handleWalletOnChange = (walletId: string) => {
    const selectedWallet = sourceWallets?.find(wallet => wallet?.lsid === walletId)

    setSendWallet(selectedWallet)
    setWalletError(false)
    setSearchWalletQuery('')
  }

  const handleOpen = (isOpen: boolean) => {
    if (!isOpen) {
      setSearchWalletQuery('')
    }
  }

  const handleContactSelect = (contact: Contact, contactInstrument: ContactInstrument) => {
    if (isEmpty(sendWallet)) {
      setWalletError(true)
      return
    }
    setContact(contact)
    setContactInstrument(contactInstrument)
    setSendStep(SendStep.DETAILS)
  }

  const handleAddContact = () => {
    setAction({
      ...action,
      actionId: ActionIds?.CONTACT,
      header: t('contacts.addCryptoContact'),
      onClose: () => {
        setIsSheetOpen(false)
        setContact(null)
      },
      meta: {
        enableCreateInstrument: true,
      },
    })
    setIsSheetOpen(true)
  }

  return (
    <>
      <Box paddingT={24} className="max-h-[75vh]">
        {/* Show error state */}
        {showErrorState && (
          <StatusCard status="error" headline={t('defaultError')}>
            <Description>
              {`${t('requestErrors.comeBackLater')} `}
              <TextAction isWindowReload text={t('requestErrors.refreshPage')} />.
            </Description>
          </StatusCard>
        )}
        {/* Show content state */}
        {!isError && (
          <>
            <Box paddingB={16}>
              <Combobox
                id="from-wallet"
                testid="from-wallet"
                items={sourceWalletOptions}
                label={t('wallets.send.sendFromWallet')}
                loading={isFetchingWallets}
                loadingInline={isFetchingWallets}
                onChange={handleWalletOnChange}
                onInputChange={setSearchWalletQuery}
                filter={false}
                defaultValue={sendWallet?.lsid ?? walletAction?.meta?.sourceWallet?.lsid}
                errorText={walletError && t('wallets.send.sendFromWalletRequired')}
                onOpen={handleOpen}
                modal
              />
            </Box>
            {showSearch && (
              <Box paddingB={16} width="full" justify="between" flex gapY={12} gapX={8}>
                <Box background="standard" width="full">
                  <Input
                    placeholderText={t('contacts.searchPlaceholder')}
                    onChange={event => setSearchQuery(event.target.value)}
                    searchable={true}
                    testId="contact-search-field"
                  />
                </Box>
                <Button testId="add-contact" variant="secondary" onClick={handleAddContact}>
                  {t('payments.addContact')}
                </Button>
              </Box>
            )}

            {isPending && (
              <ContactsListSkeletonLoader rows={5} addMeatballSkeleton={false} hasBorder={false} />
            )}

            {showNoSearchResults && (
              <NoSearchResults
                clearSearchCriteria={() => {
                  setSearchQuery('')
                }}
              />
            )}

            {!!contactInstruments?.length && (
              <Box className="w-full">
                <ContactsSearchList
                  isInstruments
                  handleContactSelect={handleContactSelect}
                  isFetchingNextPage={isFetchingNextPage}
                  contacts={contactInstruments}
                  hasLoadedMore={hasLoadedMoreContacts}
                  fetchNextPage={fetchNextPage}
                  setHasLoadedMore={setHasLoadedMoreContacts}
                  hasNextPage={hasNextPage}
                />
              </Box>
            )}
          </>
        )}
        {/* Show empty state */}
        {showEmptyState && (
          <StatusCard
            headline={t('contacts.noContactHeadline')}
            description={t('contacts.addContactDescription')}
            cardButton={{
              testid: 'add-contact',
              onClick: handleAddContact,
              iconElement: <Icon name="Plus" />,
              name: t('contacts.addContact'),
            }}
          />
        )}
      </Box>
      <ContactsSheets isSheetOpen={isSheetOpen} isNestedSheet />
    </>
  )
}
